import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { Observable, from } from 'rxjs';
import { filter, shareReplay, switchMap, take } from 'rxjs/operators';

/**
 * Centralises Firebase auth-readiness and force-refreshed ID token retrieval
 * so guards and services share one resolved auth state.
 */
@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthStateService {

  /**
   * Emits the current Firebase user once auth state resolves to a non-null user.
   * Uses shareReplay(1) so all concurrent subscribers (guards, services) wait on
   * the same resolved state rather than each triggering an independent auth check.
   */
  readonly authReady$: Observable<firebase.User> = this.afAuth.authState.pipe(
    filter(user => user !== null),
    shareReplay(1)
  );

  constructor(private afAuth: AngularFireAuth) {}

  /**
   * Returns the Firebase ID token with a forced network refresh (forceRefresh=true).
   * Use after periods of inactivity (e.g. returning to the tab after >1 hour) to
   * ensure the token is valid before the next Firebase operation. Regular in-flight
   * requests should use getIdToken() (no force) via the token interceptor.
   */
  getValidToken(): Observable<string> {
    return this.authReady$.pipe(
      take(1),
      switchMap(user => from(user.getIdToken(true)))
    );
  }
}
